<template>
    <v-dialog v-model="dialog" @click:outside="$emit('input', false)">
        <v-card>
            <v-toolbar dark color="primary">
                <v-btn icon dark @click="$emit('input', false)">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <v-toolbar-title>
                    Brand Profile: {{ item.mws_configuration_name }}
                </v-toolbar-title>
            </v-toolbar>
            <div class="d-flex pa-4">
                <calendar-filter
                    class="flex-grow-1"
                    :skip-url="true"
                    :custom-ranges="dateRanges"
                    @update="(val) => applyAsinReportFilter(val)"
                ></calendar-filter>
            </div>
            <v-list>
                <v-list-item>
                    <v-list-item-content>
                        <v-card-text v-if="!report">
                            Loading...
                            <v-progress-linear
                                indeterminate
                            ></v-progress-linear>
                        </v-card-text>

                        <highcharts
                            id="sale-chart"
                            key="sale-chart"
                            v-if="report"
                            :options="chartOptions"
                        ></highcharts>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item>
                    <v-list-item-content>
                        <highcharts
                            id="inventory-chart"
                            key="inventory-chart"
                            v-if="inventoryReport"
                            :options="inventoryChartOptions"
                        ></highcharts>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item>
                    <v-list-item-content>
                        <highcharts
                            id="order-chart"
                            key="order-chart"
                            v-if="orderReport"
                            :options="orderChartOptions"
                        ></highcharts>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-card>
    </v-dialog>
</template>

<script>
import "vue2-ez-daterange-picker/dist/vue2-daterange-picker.css";
import { sourceTz, tz } from "@/constants/Time";
import moment from "moment-timezone";
import { Chart } from "highcharts-vue";
import CalendarFilter from "@/components/CalendarFilter.vue";
import dateRanges from "@/constants/DateRanges";
import Highcharts from "highcharts";

export default {
    name: "AsinProfile",
    components: {
        CalendarFilter,
        highcharts: Chart,
    },
    props: ["marketplaceId", "customRanges", "selected", "dialog"],
    model: {
        prop: "dialog",
        event: "input",
    },
    computed: {
        selectedCreatedAt() {
            return this.item.created_at
                ? this.item.created_at.split("T")[0]
                : "";
        },
        selectedPic() {
            return this.item.pics
                ? this.item.pics.map((user) => user.name).join(", ")
                : "";
        },
        selectedSupplier() {
            return this.item.suppliers
                ? this.item.suppliers
                      .map((supplier) => supplier.name)
                      .join(", ")
                : "";
        },
    },
    data() {
        return {
            report: false,
            inventoryReport: false,
            orderReport: false,
            cachedReport: {},
            item: { ...this.selected },
            dateRanges,
            chartOptions: {
                credits: {
                    enabled: false,
                },
                chart: {
                    zoomType: "xy",
                },
                title: {
                    text: "",
                },
                subtitle: {
                    text: "",
                },
                xAxis: [
                    {
                        categories: [],
                        crosshair: true,
                    },
                ],
                yAxis: [
                    {
                        title: {
                            text: "Items",
                            style: {
                                color: Highcharts.getOptions().colors[0],
                            },
                        },
                        labels: {
                            format: "{value} items",
                            style: {
                                color: Highcharts.getOptions().colors[0],
                            },
                        },
                        opposite: true,
                    },
                    {
                        title: {
                            text: "",
                            style: {
                                color: Highcharts.getOptions().colors[1],
                            },
                        },
                        labels: {
                            format: "{value}$",
                            style: {
                                color: Highcharts.getOptions().colors[1],
                            },
                        },
                    },
                ],
                tooltip: {
                    shared: true,
                },
                legend: {
                    layout: "vertical",
                    align: "left",
                    x: 120,
                    verticalAlign: "top",
                    y: 100,
                    floating: true,
                    backgroundColor:
                        Highcharts.defaultOptions.legend.backgroundColor || // theme
                        "rgba(255,255,255,0.25)",
                },
                series: [
                    {
                        name: "Items",
                        type: "spline",
                        yAxis: 0,
                        data: [],
                        tooltip: {
                            valueSuffix: " items",
                        },
                    },
                    {
                        name: "Revenue",
                        type: "spline",
                        yAxis: 1,
                        data: [],
                        tooltip: {
                            valueSuffix: " $",
                        },
                    },
                    {
                        name: "Ad cost",
                        type: "spline",
                        yAxis: 1,
                        data: [],
                        tooltip: {
                            valueSuffix: " $",
                        },
                    },
                    {
                        name: "Net Profit",
                        type: "spline",
                        yAxis: 1,
                        data: [],
                        tooltip: {
                            valueSuffix: " $",
                        },
                    },
                ],
            },
            inventoryChartOptions: {
                credits: {
                    enabled: false,
                },
                chart: {
                    zoomType: "xy",
                },
                title: {
                    text: "",
                },
                subtitle: {
                    text: "",
                },
                xAxis: [
                    {
                        categories: [],
                        crosshair: true,
                    },
                ],
                yAxis: [
                    {
                        // Primary yAxis
                        labels: {
                            format: "{value} asin",
                            style: {
                                color: Highcharts.getOptions().colors[0],
                            },
                        },
                        title: {
                            text: "Total Asin",
                            style: {
                                color: Highcharts.getOptions().colors[0],
                            },
                        },
                    },
                    {
                        labels: {
                            format: "{value} items",
                            style: {
                                color: Highcharts.getOptions().colors[1],
                            },
                        },
                        title: {
                            text: "Total Quantity",
                            style: {
                                color: Highcharts.getOptions().colors[1],
                            },
                        },
                        opposite: true,
                    },
                    {
                        labels: {
                            format: "{value} $",
                            style: {
                                color: Highcharts.getOptions().colors[2],
                            },
                        },
                        title: {
                            text: "Total COGs",
                            style: {
                                color: Highcharts.getOptions().colors[2],
                            },
                        },
                        opposite: true,
                    },
                ],
                tooltip: {
                    shared: true,
                },
                legend: {
                    layout: "vertical",
                    align: "left",
                    x: 120,
                    verticalAlign: "top",
                    y: 100,
                    floating: true,
                    backgroundColor:
                        Highcharts.defaultOptions.legend.backgroundColor || // theme
                        "rgba(255,255,255,0.25)",
                },
                series: [
                    {
                        name: "Total Asin",
                        type: "spline",
                        data: [],
                        yAxis: 0,
                        tooltip: {
                            valueSuffix: " asin",
                        },
                    },
                    {
                        name: "Total Quantity",
                        type: "spline",
                        data: [],
                        yAxis: 1,
                        tooltip: {
                            valueSuffix: " items",
                        },
                    },
                    {
                        name: "Total COGs",
                        type: "spline",
                        data: [],
                        yAxis: 2,
                        tooltip: {
                            valueSuffix: " $",
                        },
                    },
                    // {
                    //     name: "Rev",
                    //     type: "column",
                    //     yAxis: 1,
                    //     data: [],
                    //     tooltip: {
                    //         valueSuffix: "$",
                    //     },
                    // },
                ],
            },
            orderChartOptions: {
                credits: {
                    enabled: false,
                },
                chart: {
                    zoomType: "xy",
                },
                title: {
                    text: "",
                },
                subtitle: {
                    text: "",
                },
                xAxis: [
                    {
                        categories: [],
                        crosshair: true,
                    },
                ],
                yAxis: [
                    {
                        labels: {
                            format: "{value} orders",
                            style: {
                                color: Highcharts.getOptions().colors[0],
                            },
                        },
                        title: {
                            text: "Total Orders",
                            style: {
                                color: Highcharts.getOptions().colors[0],
                            },
                        },
                    },
                    {
                        labels: {
                            format: "{value} items",
                            style: {
                                color: Highcharts.getOptions().colors[1],
                            },
                        },
                        title: {
                            text: "Total Items",
                            style: {
                                color: Highcharts.getOptions().colors[1],
                            },
                        },
                        opposite: true,
                    },
                ],
                tooltip: {
                    shared: true,
                },
                legend: {
                    layout: "vertical",
                    align: "left",
                    x: 120,
                    verticalAlign: "top",
                    y: 100,
                    floating: true,
                    backgroundColor:
                        Highcharts.defaultOptions.legend.backgroundColor ||
                        "rgba(255,255,255,0.25)",
                },
                series: [
                    {
                        name: "Total Orders",
                        type: "spline",
                        data: [],
                        yAxis: 0,
                        tooltip: {
                            valueSuffix: " orders",
                        },
                    },
                    {
                        name: "Total Items",
                        type: "spline",
                        data: [],
                        yAxis: 1,
                        tooltip: {
                            valueSuffix: " items",
                        },
                    },
                ],
            },
        };
    },
    watch: {
        dialog(newVal) {
            if (!newVal) {
                return;
            }

            this.item = { ...this.selected };

            this.applyAsinReportFilter({
                startDate: moment()
                    .subtract(30, "day")
                    .tz(sourceTz)
                    .startOf("day"),
                endDate: moment().tz(sourceTz).startOf("day"),
            });
        },
    },
    methods: {
        async applyAsinReportFilter(value) {
            let filter = {
                mws_configuration_id: this.item.mws_configuration_id,
                start: value.startDate.clone().format("YYYY-MM-DD"),
                end: value.endDate.clone().format("YYYY-MM-DD"),
            };

            this.report = false;
            this.inventoryReport = false;
            this.orderReport = false;
            if (
                this.cachedReport[
                    `${filter.mws_configuration_id}_${filter.start}_${filter.end}`
                ]
            ) {
                this.report =
                    this.cachedReport[
                        `${filter.mws_configuration_id}_${filter.start}_${filter.end}`
                    ];
                this.inventoryReport = { ...this.report };
                this.orderReport = { ...this.report };
            } else {
                const response = await this.$store.dispatch(
                    "ReportItemProfileReport/getList",
                    {
                        filter,
                    }
                );
                this.report = response.data;
                this.inventoryReport = { ...this.report };
                this.orderReport = { ...this.report };
                this.cachedReport[
                    `${filter.mws_configuration_id}_${filter.start}_${filter.end}`
                ] = { ...response.data };
            }
            this.chartOptions.xAxis[0].categories = this.report.total_item.map(
                (i) => {
                    if (i.year) {
                        return moment()
                            .year(i.year)
                            .month(i.month - 1)
                            .tz(tz)
                            .format("MMM YYYY");
                    }
                    return moment(i.at_date).tz(tz).format("ll");
                }
            );
            this.chartOptions.series[0].data = this.report.total_item.map(
                (i) => 1 * i.count
            );
            this.chartOptions.series[1].data = this.report.total_revenue.map(
                (i) => 1 * i.count
            );

            this.chartOptions.series[2].data =
                this.report.total_advertising_cost.map((i) => 1 * i.count);
            this.chartOptions.series[3].data = this.report.total_net_profit.map(
                (i) => 1 * i.count
            );

            this.inventoryChartOptions.xAxis[0].categories =
                this.report.total_asin.map((i) => {
                    if (i.year) {
                        return moment()
                            .year(i.year)
                            .month(i.month - 1)
                            .tz(tz)
                            .format("MMM YYYY");
                    }
                    return moment(i.at_date).tz(tz).format("ll");
                });

            this.inventoryChartOptions.series[0].data =
                this.report.total_asin.map((i) => 1 * i.count);

            this.inventoryChartOptions.series[1].data =
                this.report.total_quantity.map((i) => 1 * i.count);

            this.inventoryChartOptions.series[2].data =
                this.report.total_cogs.map((i) => 1 * i.count);

            this.orderChartOptions.xAxis[0].categories =
                this.report.total_order.map((i) => {
                    if (i.year) {
                        return moment()
                            .year(i.year)
                            .month(i.month - 1)
                            .tz(tz)
                            .format("MMM YYYY");
                    }
                    return moment(i.at_date).tz(tz).format("ll");
                });

            this.orderChartOptions.series[0].data =
                this.report.total_order.map((i) => 1 * i.count);

            this.orderChartOptions.series[1].data =
                this.report.total_item.map((i) => 1 * i.count);
        },
    },
};
</script>
